import { repositoryClient, toRepositoryBody } from "@/core/network/repository-client";
import type {
  AvailableSlotsResponse,
  Booking,
  ConsultationMode,
  CreateSlotHoldPayload,
  HealerSessionOption,
  PaymentOrder,
  SlotHold,
} from "@/features/booking/types/booking";
import type {
  AvailableSlotsResponseDto,
  BookingDto,
  HealerSessionOptionDto,
  PaymentOrderDto,
  SubmitConcernRequestDto,
  SlotHoldDto,
} from "../dto/booking.dto";
import {
  mapAvailableSlotsDto,
  mapBookingDto,
  mapHealerSessionOptionsDto,
  mapPaymentOrderDto,
  mapSlotHoldDto,
} from "../mappers/booking.mapper";

/** Public — no seeker auth required. */
export async function getAvailableSlots(
  healerId: string,
  durationMinutes?: number,
  from?: string,
  to?: string,
): Promise<AvailableSlotsResponse> {
  const dto = await repositoryClient.get<AvailableSlotsResponseDto>(
    "/healers/:healerId/available-slots",
    {
      pathParams: { healerId },
      queryParams: {
        ...(durationMinutes ? { durationMinutes: String(durationMinutes) } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      },
      useAuth: false,
    },
  );

  return mapAvailableSlotsDto(dto);
}

/** Public — the healer's bookable duration/price/mode combinations. */
export async function getSessionOptions(healerId: string): Promise<HealerSessionOption[]> {
  const dto = await repositoryClient.get<HealerSessionOptionDto[]>(
    "/healers/:healerId/session-options",
    {
      pathParams: { healerId },
      useAuth: false,
    },
  );

  return mapHealerSessionOptionsDto(dto);
}

/** Authenticated — places a short-lived hold on a slot for the current seeker. */
export async function createSlotHold(
  healerId: string,
  payload: CreateSlotHoldPayload,
): Promise<SlotHold> {
  const dto = await repositoryClient.post<SlotHoldDto>("/healers/:healerId/slot-holds", {
    pathParams: { healerId },
    body: toRepositoryBody(payload),
    useAuth: true,
  });

  return mapSlotHoldDto(dto);
}

/** Authenticated — attaches the seeker's concern details to an existing hold. */
export async function submitConcern(
  holdId: string,
  concern: Record<string, unknown>,
): Promise<SlotHold> {
  const body: SubmitConcernRequestDto = { concern };
  const dto = await repositoryClient.patch<SlotHoldDto>("/seekers/me/slot-holds/:id/concern", {
    pathParams: { id: holdId },
    body: toRepositoryBody(body),
    useAuth: true,
  });

  return mapSlotHoldDto(dto);
}

/** Authenticated — creates the Razorpay payment order for a held slot. */
export async function createPaymentOrder(holdId: string): Promise<PaymentOrder> {
  const dto = await repositoryClient.post<PaymentOrderDto>(
    "/seekers/me/slot-holds/:id/payment-order",
    {
      pathParams: { id: holdId },
      useAuth: true,
    },
  );

  return mapPaymentOrderDto(dto);
}

/** Authenticated — polled by the confirmation page until payment settles. */
export async function getBookingStatus(bookingId: string): Promise<Booking> {
  const dto = await repositoryClient.get<BookingDto>("/seekers/me/bookings/:id/status", {
    pathParams: { id: bookingId },
    useAuth: true,
  });

  return mapBookingDto(dto);
}

// Re-exported so callers that only need the mode union don't need to reach
// into ../../types directly.
export type { ConsultationMode };
